import {
  Box,
  NumberFormatter,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { FormattedMessage } from "react-intl";
import type {
  AllocationChartItem,
  ManagedAllocationCardProps,
} from "../../types";
import { AllocationBarChart } from "../allocationBarChart";
import { isMobileDevice } from "../../utils/useDeviceDetection";
import classes from "./index.module.css";
import { REPLAY_BLOCK_CLASS } from "../../utils/statsig";

const isMobile = isMobileDevice();

export function ManagedAllocationCard({ data }: ManagedAllocationCardProps) {
  // The same fund held in several managed policies is one slice.
  const merged = new Map<string, AllocationChartItem>();
  (data?.managedAllocation?.assetAllocation ?? []).forEach((asset) => {
    const name = asset.name || asset.buildingblockName || "Okänd tillgång";
    const key = asset.isin || name;
    const existing = merged.get(key);
    if (existing) {
      existing.share += asset.percentage;
    } else {
      merged.set(key, {
        name,
        share: asset.percentage,
        isin: asset.isin || undefined,
      });
    }
  });
  const items = Array.from(merged.values());

  if (items.length === 0) return null;

  const value = (
    <Box>
      <Text c="dimmed" size="sm" mb={4}>
        <FormattedMessage
          id="dashboard.allocationCard.value"
          defaultMessage="Värde på denna del"
        />
      </Text>
      <Text fw={700}>
        <NumberFormatter
          className={REPLAY_BLOCK_CLASS}
          value={data?.managedAllocation?.totalAssetValue ?? 0}
          decimalScale={0}
          thousandSeparator=" "
          prefix="ca "
          suffix=" SEK"
        />
      </Text>
    </Box>
  );
  const pensionContribution = (
    <Box>
      <Text c="dimmed" size="sm" mb={4}>
        <FormattedMessage
          id="dashboard.allocationCard.pensionContribution"
          defaultMessage="Bidrag till din förväntade pension"
        />
      </Text>
      <Text fw={700}>
        <NumberFormatter
          className={REPLAY_BLOCK_CLASS}
          value={Math.abs(data?.managedAllocation?.firstMonthPension ?? 0)}
          decimalScale={0}
          thousandSeparator=" "
          suffix=" SEK / månad"
        />
      </Text>
    </Box>
  );

  return (
    <Stack gap="md" className={classes.card} data-testid="managed-allocation">
      <Title ff="var(--wallet-font-family-sambla-sans)" order={3} size="h4">
        <FormattedMessage
          id="dashboard.allocationCard.title"
          defaultMessage="Den del som är under rådgiven förvaltning av Sambla är placerad så här"
        />
      </Title>

      <AllocationBarChart items={items} />

      {isMobile ? (
        <Stack gap="md" className={classes.values}>
          {value}
          {pensionContribution}
        </Stack>
      ) : (
        <SimpleGrid cols={2} spacing="md" className={classes.values}>
          {value}
          {pensionContribution}
        </SimpleGrid>
      )}
    </Stack>
  );
}
